import { ReactNode, useEffect, useRef } from "react";

import { useLocation } from "react-router";

import { useErrorStore } from "@/stores/useErrorStore";
import { resetAllTablesExceptPaginationAndColumns } from "@/stores/useTableStore";
import * as Sentry from "@sentry/react";

import { ROUTES } from "../..";
import errorAnimation from "../../../assets/animations/error_animation.json";
import ErrorComponent from "./components/ErrorComponent";
import { TITLES } from "./types";

interface IErrorBoundaryProps {
  children: ReactNode;
}

interface IErrorFallbackProps {
  resetError: () => void;
}

const handleButtonClick = (pathname: string) => {
  const newState = { page: "redirect" };

  pathname === ROUTES.Dashboard
    ? globalThis.location.reload()
    : globalThis.history.replaceState(newState, "Redirect", ROUTES.Dashboard);

  globalThis.location.replace(ROUTES.Dashboard);

  resetAllTablesExceptPaginationAndColumns();
};

const ErrorFallback = ({ resetError }: IErrorFallbackProps) => {
  const location = useLocation();
  const originalPathname = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== originalPathname.current) {
      resetError();
    }
  }, [location.pathname, resetError]);

  return (
    <ErrorComponent
      onButtonClick={() => handleButtonClick(location.pathname)}
      title={TITLES.Error}
      animation={errorAnimation}
      message={"Error##message wait"}
      pathname={location.pathname}
    />
  );
};

const ErrorBoundaryInner = ({ children }: IErrorBoundaryProps) => {
  const location = useLocation();
  const { serverError, networkError, setIsServerError, setIsNetworkError } =
    useErrorStore();

  useEffect(() => {
    setIsServerError(undefined);
    setIsNetworkError(undefined);
  }, [location.pathname, setIsServerError, setIsNetworkError]);

  if (Boolean(serverError) || Boolean(networkError)) {
    return (
      <ErrorComponent
        onButtonClick={() => handleButtonClick(location.pathname)}
        title={TITLES.Error}
        animation={errorAnimation}
        message={"Error##message wait"}
        pathname={location.pathname}
      />
    );
  }

  return <>{children}</>;
};

const ErrorBoundary = ({ children }: IErrorBoundaryProps) => (
  <Sentry.ErrorBoundary fallback={ErrorFallback}>
    <ErrorBoundaryInner>{children}</ErrorBoundaryInner>
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;
